/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { routePaths } from '@/constants/routePaths';
import { selectOrderDetailsFromState } from '@/constants/selectors';
import type { QOHErrorAnalytics, QohErrorAnalyticsData } from '@/types/analytics';
import {
  CMSModularHandler,
  hasCMSPremiumTable,
  SEOProductSchema,
  ProductDetailMetadata,
} from '@/features/pdp';
import { useContentStackDefaultPDPContent } from '@/features/pdp/api/getContentStackDefaultProductContent';
import { useContentStackPremiumPDPContent } from '@/features/pdp/api/getContentStackPremiumProductContent';
import { clickTrack } from '@/utils/analytics/clickTrack';
import { analyticsPageType } from '@/constants/analytics/analyticsPageType';
import { type AddCertonaItemObjectType } from '@/features/certona';
import { useAddToCartWithMiniCart } from '@/features/certona/hooks/useAddToCartWithMiniCart';
import { useGetCertonaRecommendations } from '@/features/certona/api/useGetCertonaRecommendations';
import { certonaPageType } from '@/constants/certonaPageType';
import { FeatureFlag, useFeatureFlag } from '@/features/globalConfig';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useIsStoreSelected } from '@/features/header/hooks/useIsStoreSelected';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';
import { PreFooter } from '@/features/prefooter';
import { useProductSkuDeals } from '@/features/product/api/getProductDeals';
import { useSylvaniaVideos } from '@/features/product/api/getSylvaniaVideos';
import { useAcesSylvaniaVideos } from '@/features/product/api/getAcesSylvaniaVideo';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { useProductSkuDetails } from '@/features/product/api/getProductSkuDetails';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useRouter } from 'next/navigation';
import type { ReduxState } from '@/types';
import { convertOrderTypeToFulfillmentType } from '@/utils/itemHelpers/lineItemHelpers/convertOrderTypeToFulfillmentType';
import { getMaxAvailQtyFromOptions } from '@/utils/itemHelpers/fulfillmentHelpers/getMaxAvailQtyFromOptions';
import { sessionStorage } from '@/utils/sessionStorage';
import { getPathnameFromAsPath, stringifyUrl } from '@/utils/urlHelpers';
import { useEffect, useState, Suspense, useMemo, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import root from 'window-or-global';
import LeftSectionSkeletonLoading from '../../components/ProductDetail/ProductAvailabilityInfo/SkeletonLoading/LeftSection';
import RightSectionSkeletonLoading from '../../components/ProductDetail/ProductAvailabilityInfo/SkeletonLoading/RightSection';
import actionTypes from '../../types/action';
import usePrevious from '../../utils/usePrevious';
import ErrorPage from '../ErrorPage';
import Hidden from '../Hidden';
import { accessoriesOptions } from './accessoriesOptions';
import ProductAvailabilityInfo from './ProductAvailabilityInfo/';
import ProductAvailabilityInfoV2 from './ProductAvailabilityInfo/ProductAvailabilityInfoV2';
import ProductAvailabilityInfoSkeleton from './ProductAvailabilityInfo/ProductAvailabilityInfoSkeleton';
import styles from './styles.module.scss';
import { DealDrawer } from '@/features/deals/components/DealDrawer/DealDrawer';
import { type FULFILLMENT_METHODS } from '@/constants/fulfillmentConstants';
import { DynamicLinks } from '@/features/dlm/components/DynamicLinks';
import { useDispatch } from '@/hooks/redux/useDispatch';
import { Drawer } from '../Drawer/Drawer';
import { TrapFocus } from '../Dialog/Modal/TrapFocus';
import { useAutoCloseOnOrderReady } from '@/features/sisterStoreFlyout';
import dynamic from 'next/dynamic';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';
import { formatPartTypeId } from '@/utils/formatPartTypeId';
import { View, Skeleton } from '@az/starc-ui';
import { usePaymentOptionsMutation } from '@/features/checkout/api/getPaymentOptions';
import { getFormattedPriceMultiplied, getStaticFormattedPrice } from '@/utils/priceHelpers';
import { useHandleRedirect } from '@/features/pdp/hooks/useHandleRedirect';
import { type ShippingType } from '@/features/fulfillment';
import { useContentStackLivePreview } from '@/hooks/useContentStackLivePreview';
import { useMonetateDecisionFlag } from '@/features/kibo/api/getKiboDecision';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';
import { usePdpAnalytics } from '@/features/pdp/hooks/usePdpAnalytics';
import { keepPreviousData } from '@tanstack/react-query';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { usePrefetchAffinityACES } from '@/features/pdpAffinity/api/getAffinityRelatedProductsACES';
import { type ProductModelACES } from '@/api/types/products-types';
import { useCertonaAddToCartFlag } from '@/hooks/useCertonaAddToCartCTAFlag';
import { useRecommendations } from '@/features/recommendations';
import { MINI_CART_TITLE_ID } from '@/features/miniCartFlyout';
import LeftColumnSkeleton from './ProductAvailabilityInfo/SkeletonLoadingV2/LeftColumnSkeleton';
import { MiddleColumnSkeleton } from './ProductAvailabilityInfo/SkeletonLoadingV2/MiddleColumnSkeleton/MiddleColumnSkeleton';
import ProductAvailabilityInfoSkeletonV2 from './ProductAvailabilityInfo/ProductAvailabilityInfoSkeletonV2';
import RightColumnSkeleton from './ProductAvailabilityInfo/SkeletonLoadingV2/RightColumnSkeleton';
import MiddleColumnSkeletonTop from './ProductAvailabilityInfo/SkeletonLoadingV2/MiddleColumnSkeleton/MiddleColumnSkeletonTop';
import MiddleColumnSkeletonBottom from './ProductAvailabilityInfo/SkeletonLoadingV2/MiddleColumnSkeleton/MiddleColumnSkeletonBottom';
import { useGetVehicleInstallGuide } from '@/features/pdp/api/getVehicleInstallGuide';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { useCatalogConfigs } from '@/features/common/api/getCatalogConfig';
import { useContentStackEnhancedPDPContent } from '@/features/pdp/api/getContentStackEnhancedProductContent';
import { stitchEnhancedPDPContent } from '@/features/contentstack/utils/stitchEnhancedPDPContent';

const LazyMiniCart = dynamic(
  () => import('../Cart/MiniCartModal').then((mod) => mod.MiniCartModal),
  {
    loading: () => <ProgressModal noScroll />,
  },
);

const LazyMiniCartFlyoutComp = dynamic(
  () =>
    import('@/features/miniCartFlyout/components/MiniCartFlyoutComp').then(
      (mod) => mod.MiniCartFlyoutComp,
    ),
  {
    loading: () => <ProgressModal noScroll />,
  },
);

const LazyProductCustomizableSKU = dynamic(
  () =>
    import('@/features/pdp/components/ProductCustomizableSKU').then(
      (mod) => mod.ProductCustomizableSKU,
    ),
  {
    loading: () => <ProgressModal noScroll />,
  },
);

type WarrantyModal = {
  isShown: boolean;
  showModalFrom?: string;
  warrantyType?: string;
};

const trackSortFilter = () => {
  clickTrack({
    eventType: 'sortReviewsOptionsInteractionPDP',
  });
};

const trackPdpQoHFailure = (qohErrorAnalyticsData: QohErrorAnalyticsData) => {
  const { pageName = '', pageType = '', siteSection = '' } = root.utag_data || {};
  const data: QOHErrorAnalytics = {
    eventType: 'addToCartQohError',
    pageName,
    pageType,
    siteSection,
    originalPartTypeIdList: [qohErrorAnalyticsData.originalPartTypeId ?? ''],
    productAvailableQuantityList: [qohErrorAnalyticsData.productAvailableQuantity.toString() ?? ''],
    productBrandList: qohErrorAnalyticsData.productBrandList,
    productCorePriceList: qohErrorAnalyticsData.productCorePriceList,
    productDiscountPriceList: [''],
    productFulfillmentTypeList: [
      convertOrderTypeToFulfillmentType(qohErrorAnalyticsData.orderType).toString() ?? '',
    ],
    productIdList: [qohErrorAnalyticsData.skuNumber ?? ''],
    productPartList: [''],
    productPartNumberList: [qohErrorAnalyticsData.partNumber ?? ''],
    productPriceList: [qohErrorAnalyticsData.productPrice.toString() ?? ''],
    productRepositoryIdList: qohErrorAnalyticsData.productRepositoryId?.toString() ?? '',
    productRequestedQuantityList: [qohErrorAnalyticsData.requestQuantity.toString()],
    qohErrorMessage: [qohErrorAnalyticsData.errorMessage],
    qohErrorMsgType: ['PDP QOH'],
  };
  clickTrack(data);
};

function ProductDetail() {
  const {
    addToCartCall,
    clearProductPageData,
    changeFindProduct,
    addingToCartStatus,
    productData,
    cartSuccess,
    err: addToCartErr,
    setQuantityInCart,
  } = useAddToCartWithMiniCart();
  const { fulfillmentTypeId, quantity, cartSuccessNote, quantityInCart } = productData;

  const isCertonaEnabledForOosOrDoesNotFit =
    useFeatureFlag('IS_OOS_OR_DOES_NOT_FIT_CONTAINER_ENABLED') === 'true';
  const { acesEnabled } = useAcesFlags();
  usePrefetchAffinityACES();
  const isProductDetailsPageV2Enabled = useForteDecisionFlagWithTrueDefault('pdpRedesignEnabled');
  const isEnhancedPdpEnabled = useForteDecisionFlagWithTrueDefault('enhancedPdpEnabled');

  const preferredVehicle = usePreferredVehicle();
  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  const dispatch = useDispatch();
  const { mutateAsync: fetchPaymentOptions } = usePaymentOptionsMutation();
  const lazyMiniCartModalRef = useRef<HTMLDivElement>(null);
  const [analyticsExecuted, setAnalyticsExecuted] = useState(false);
  const [miniCartFlyoutVisible, setMiniCartFlyoutVisible] = useState(false);
  const [certonaRecommendationAddedToCart, setCertonaRecommendationAddedToCart] = useState(false);
  const toggleMiniCartFlyout = () => {
    setMiniCartFlyoutVisible(() => !miniCartFlyoutVisible);
    closeStoreModal();
  };

  const toggleCertonaMiniCartFlyout = () => {
    setShowCertonaMiniCartFlyoutModal(() => !showCertonaMiniCartFlyoutModal);
    closeStoreModal();
  };

  const locale = useLocale();
  const [warrantyModal, setWarrantyModal] = useState<WarrantyModal>({ isShown: false });
  const [shouldOpenSpecsPanel, setShouldOpenSpecsPanel] = useState(false);
  const [recommendationsClicked, setRecommendationsClicked] = useState(false);
  const [customizeModalOpen, setCustomizeModalOpen] = useState(false);
  const [addedCertonaItemObject, setAddedCertonaItemObject] =
    useState<AddCertonaItemObjectType | null>(null);
  const [showCertonaMiniCartFlyoutModal, setShowCertonaMiniCartFlyoutModal] =
    useState<boolean>(false);
  /*XM data*/
  const cartSuccessData = cartSuccess;

  const { data: headerData } = useHeaderData();
  const miniCartMap = headerData?.miniCartMap;
  const minicart = headerData?.miniCartSuccessNote;
  const storeNumber = headerData?.storeNumber;
  const vehicleMap = headerData?.vehicleMap;
  const vehicleId = acesEnabled ? (vehicleMap?.vehicleId ?? '') : vehicleMap?.catalogVehicleId;
  const vehicleDataHeader = headerData?.vehicleMap?.acesVehicleAttributes?.vehicleQuestionsNames;
  const vehicleFuelTypeId =
    headerData?.vehicleMap?.acesVehicleAttributes?.vehicleQuestionsIds?.fuelTypeId;
  const {
    data: productDetailsQueryData,
    refetch: refetchProductData,
    isFetched: isProductDetailsQueryFetched,
    isFetching: isProductDetailsQueryFetching,
    isSuccess: isProductDetailsQuerySuccess,
    isError: isProductDetailsQueryError,
    dataUpdatedAt: productDetailsQueryDataUpdatedAt,
  } = useProductDetails({ placeholderData: keepPreviousData });
  const productDetails = productDetailsQueryData?.product;

  const hasVehicle = acesEnabled
    ? Boolean(headerData?.vehicleMap?.vehicleName)
    : !!headerData?.vehicleMap?.vehicleId && headerData?.vehicleMap?.vehicleId !== '';

  const taxonomyPath = productDetails?.cmsContentPath ?? productDetails?.taxonomyUrl ?? '';

  const brandName = productDetails?.subBrandName ?? productDetails?.brandName ?? '';

  const isCSPDPEnabled = isProductDetailsQueryFetched && !isProductDetailsQueryFetching;
  const {
    data: defaultPDPContent,
    isFetched: isDefaultPDPFetched,
    isFetching: isDefaultPDPFetching,
  } = useContentStackDefaultPDPContent({
    enabled: isCSPDPEnabled,
    taxonomyPath,
  });

  // Enhanced PDP replaces Premium PDP — when enhanced is enabled, premium is never used
  const isPremiumPDPEnabled =
    !isEnhancedPdpEnabled &&
    isCSPDPEnabled &&
    isDefaultPDPFetched &&
    (defaultPDPContent?.premiumBrands?.brands as string[])?.includes(brandName);
  const {
    data: premiumPDPContent,
    isFetched: isPremiumPDPFetched,
    isPending: isPremiumPDPPending,
  } = useContentStackPremiumPDPContent({
    enabled:
      !isEnhancedPdpEnabled &&
      isCSPDPEnabled &&
      isDefaultPDPFetched &&
      !isDefaultPDPFetching &&
      (defaultPDPContent?.premiumBrands?.brands as string[])?.includes(brandName),
    taxonomyPath,
    brandName,
  });

  useContentStackLivePreview({ enabled: isCSPDPEnabled });
  const isPDPContentFetched =
    !isPremiumPDPPending && (isDefaultPDPFetched || (isPremiumPDPFetched && taxonomyPath));

  const productSkuId = useMemo(() => {
    return isProductDetailsQuerySuccess && productDetails?.itemId ? [productDetails?.itemId] : null;
  }, [isProductDetailsQuerySuccess, productDetails?.itemId]);
  const { data: enhancedPDPContent, isFetched: isEnhancedPDPFetched } =
    useContentStackEnhancedPDPContent({
      taxonomyPath,
      brandName,
      sku: productDetails?.itemId,
      enabled: isEnhancedPdpEnabled,
    });

  const stitchedPageBody =
    defaultPDPContent?.pageBody && enhancedPDPContent?.response && isEnhancedPdpEnabled
      ? stitchEnhancedPDPContent(defaultPDPContent.pageBody, enhancedPDPContent.response)
      : defaultPDPContent?.pageBody;

  const hasEnhancedContent =
    isEnhancedPdpEnabled &&
    !!(
      enhancedPDPContent?.response?.top ||
      enhancedPDPContent?.response?.middle ||
      enhancedPDPContent?.response?.bottom
    );

  const isEnhancedPdpQueryEnabled = isEnhancedPdpEnabled && !!taxonomyPath;
  const isEnhancedPDPReady = !isEnhancedPdpQueryEnabled || isEnhancedPDPFetched;

  const currentContent = isEnhancedPdpEnabled
    ? stitchedPageBody
    : (premiumPDPContent?.pageBody ?? defaultPDPContent?.pageBody);

  const installVideosEnabled =
    useFeatureFlag('IS_YMME_INSTALL_VIDEOS_ENABLED') === 'true' &&
    productDetails?.lineCode === 'SYL' &&
    !!preferredVehicle;
  // TODO: consider using catalog-configs instead of searching in the array
  const partTypeHasVehicleImages =
    defaultPDPContent?.partTypesWithVehicleImages?.includes(
      productDetails?.originalPartTypeId ?? '',
    ) ?? false;
  const isNewDealsApiEnabled = useFeatureFlag('IS_NEW_DEALS_API_ENABLED') === 'true';

  const { data: partsProSylvaniaData, isSuccess: partsProSylvaniaRequestIsSuccess } =
    useSylvaniaVideos({
      enabled: installVideosEnabled && !acesEnabled,
      productId: productDetails?.itemId ?? '',
    });

  const { data: aceSylvaniaData, isSuccess: aceSylvaniaRequestIsSuccess } = useAcesSylvaniaVideos({
    enabled: installVideosEnabled && acesEnabled,
    skuId: productDetails?.itemId ?? '',
  });

  const sylvaniaRequestIsSuccess = acesEnabled
    ? aceSylvaniaRequestIsSuccess
    : partsProSylvaniaRequestIsSuccess;

  const sylvaniaData = acesEnabled ? aceSylvaniaData : partsProSylvaniaData;
  const isVehicleInstallGuideVideoEnabled = useForteDecisionFlagWithTrueDefault(
    'vehicleInstallGuideVideoEnabled',
  );

  const { data: catalogConfigsData } = useCatalogConfigs({
    itemId: productDetails?.partGroupId || '',
    itemType: 'product',
    enabled: isVehicleInstallGuideVideoEnabled,
  });

  const country = getCountryFromLocale(locale);
  const installationLocation = catalogConfigsData?.configs?.installationProcedureName;
  const { data: vehicleInstallGuideData, isFetching } = useGetVehicleInstallGuide({
    country,
    year: vehicleMap?.year ?? '',
    makeName: vehicleDataHeader?.makeName ?? '',
    modelName: vehicleDataHeader?.modelName ?? '',
    engineName: vehicleDataHeader?.engineName ?? '',
    location: installationLocation ?? '',
    fuelTypeId: vehicleFuelTypeId ?? 0,
    enabled:
      isVehicleInstallGuideVideoEnabled &&
      !!productDetails?.vehicleFitFlag &&
      !!vehicleDataHeader?.makeName &&
      !!vehicleDataHeader?.modelName &&
      !!vehicleDataHeader?.engineName &&
      !!installationLocation,
  });
  const vehicleInstallationGuideVideos = vehicleInstallGuideData?.videos ?? [];
  const vehicleInstallGuideRequestFetch = !isFetching;

  const productDetailsSEOMetadata = productDetails?.seoMetaData as Record<string, any> | undefined;

  const skuDetailsResult = useProductSkuDetails({
    skuIds: productSkuId,
  });
  const { refetch: refetchSkuDetails } = skuDetailsResult;
  const { data: skuDealsData } = useProductSkuDeals({
    skuIds: productSkuId,
    enabled: isNewDealsApiEnabled,
  });
  const skuDetailsData = skuDetailsResult.isSuccess ? skuDetailsResult.data[0] : null;
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const isTablet = useMediaQuery((theme) => theme.breakpoints.only('md'));
  const isMobile = useMediaQuery((theme) => theme.breakpoints.only('sm'));
  const [dealsAddedToCart, setDealsAddedToCart] = useState(false);
  const [showBundleDeals, setShowBundleDeals] = useState(false);
  const [count, setCount] = useState(skuDetailsData?.quantityInCart || 1);

  const staticPrice = useMemo(() => {
    const currentPrice = getStaticFormattedPrice(
      skuDetailsData?.skuPricingAndAvailability.configurableSku,
      skuDetailsData?.skuPricingAndAvailability,
    );
    return Number(currentPrice);
  }, [skuDetailsData?.skuPricingAndAvailability]);

  const [retailPrice, setRetailPrice] = useState<number | string>(() =>
    getFormattedPriceMultiplied(staticPrice, count),
  );
  const [groupId, setGroupId] = useState<
    typeof FULFILLMENT_METHODS.STOREORDER | typeof FULFILLMENT_METHODS.ONLINEORDER
  >(0);
  const desktopMiniCartFlyoutEnabled =
    useFeatureFlag('IS_DESKTOP_MINI_CART_FLYOUT_ENABLED') === 'true';
  const mobileMiniCartFlyoutEnabled =
    useFeatureFlag('IS_MOBILE_MINI_CART_FLYOUT_ENABLED') === 'true';
  const tabletMiniCartFlyoutEnabled =
    useFeatureFlag('IS_TABLET_MINI_CART_FLYOUT_ENABLED') === 'true';

  const discountedPrice = isNewDealsApiEnabled
    ? skuDealsData?.[0]?.skuDealsInfo?.discountedPrice
    : skuDetailsData?.skuPricingAndAvailability?.discountedPrice;
  const strikeThroughPricingFlag = useFeatureFlag('SHOW_STRIKE_THROUGH_PRICING') === 'true';
  const showStrikeThroughPricing: boolean = strikeThroughPricingFlag && !!discountedPrice;

  const shouldShowDealsButton =
    useForteDecisionFlagWithTrueDefault('dealBadgeEnabled') &&
    skuDetailsData?.storeSelected &&
    !showStrikeThroughPricing;

  const itemFulfillmentOptions =
    skuDetailsData?.skuPricingAndAvailability?.skuAvailabilityInfo?.fulfillmentOptions;
  const hasStoreSelected = useIsStoreSelected();

  // Check if product is not available for BOPUS or STH (regardless of sister store availability)
  const bopusAvailable = skuDetailsData?.skuPricingAndAvailability?.storePickupAvailable ?? false;
  const sthAvailable = skuDetailsData?.skuPricingAndAvailability?.shipToHomeAvailable ?? false;
  const productNotAvailable = !bopusAvailable && !sthAvailable;

  const isOos =
    hasStoreSelected &&
    (hasVehicle || (!hasVehicle && productDetails?.recordType !== 'app')) &&
    itemFulfillmentOptions?.some((item) => item.available || item.sisterStoreAvailable) === false;

  const doesNotFit =
    hasStoreSelected &&
    hasVehicle &&
    productDetails?.recordType === 'app' &&
    !productDetails.vehicleFitFlag;

  const redirectPath = productDetailsQueryData?.redirectURL;

  const superSkuRedirectPath = undefined;

  useHandleRedirect({ redirectPath: redirectPath ?? superSkuRedirectPath });

  const orderDetailsReadyStatus = useSelector(
    (state: ReduxState) => state?.orders?.details?.readyStatus,
  );

  const pdpBreadcrumb = productDetails?.breadCrumbs;
  const parentShelfPage = pdpBreadcrumb?.find((item) => item.pageType === 'ProductShelf');
  const fitsVehicle = productDetails?.vehicleFitFlag ?? false;
  const vehicleFit = (productDetails as ProductModelACES)?.vehicleFit;
  const vehicleFitmentLabel = (productDetails as ProductModelACES)?.vehicleFitment;
  /*
     The PDP currently fetches product data on
     the server again after adding an item to cart.
     Certona shouldn't be called again, so this flag is used.
     */
  const [disableCertonaCall, setDisableCertonaCall] = useState(false);
  const defaultFulfillmentOption = useFeatureFlag('DEFAULT_FULFILLMENT_OPTION') || '';
  const crumInfo = (pdpBreadcrumb?.[0]?.displayName ?? '').toLowerCase();
  const isAccessories = accessoriesOptions.includes(crumInfo);
  const orderDetailsFromState = useSelector(selectOrderDetailsFromState);
  const [trackQOHError, setTrackQOHError] = useState(false);
  const [shippingType, setShippingType] = useState<ShippingType>('' as ShippingType);
  const [shippingTypeChanged, setShippingTypeChanged] = useState(false);
  const [storeChanged, setStoreChanged] = useState(false);
  const [fulfillmentId, setFulfillmentId] = useState(0);
  const [oemPartNumber, setOemPartNumber] = useState<string>();
  const [oemBrandName, setOemBrandName] = useState<string>();
  const prevShippingType = usePrevious(shippingType);
  const productAvailableQuantity =
    itemFulfillmentOptions && getMaxAvailQtyFromOptions(itemFulfillmentOptions);
  const isRedirect = Boolean(productDetailsQueryData?.redirectURL);
  const isPageLoading =
    !currentContent || (isPremiumPDPEnabled && !isPremiumPDPFetched) || isRedirect;

  const canRenderCMSComparisonChart: boolean = useMemo(() => {
    if (isProductDetailsQueryFetched && isDefaultPDPFetched) {
      // TODO: Once we fully Enable Enhanced PDP Content This logic and places with similar logic needs to be removed/revisited
      const premiumBrands: string[] = defaultPDPContent?.premiumBrands?.brands ?? [];

      return premiumBrands?.includes(brandName)
        ? isEnhancedPdpEnabled ||
            (isPremiumPDPFetched && !premiumPDPContent?.pageBody?.some(hasCMSPremiumTable))
        : true;
    }
    return false;
  }, [
    isProductDetailsQueryFetched,
    isDefaultPDPFetched,
    isPremiumPDPFetched,
    defaultPDPContent?.premiumBrands?.brands,
    brandName,
    premiumPDPContent?.pageBody,
    isEnhancedPdpEnabled,
  ]);

  usePdpAnalytics({
    recommendationsClicked,
    installVideosEnabled,
    sylvaniaRequestIsSuccess,
    addingToCartStatus,
    analyticsExecuted,
    discountedPrice,
    vehicleId,
    fitsVehicle,
    shouldShowDealsButton,
    sylvaniaData,
    partTypeHasVehicleImages,
    setAnalyticsExecuted,
    vehicleFit,
    vehicleFitmentLabel,
    vehicleInstallationGuideVideos,
    vehicleInstallGuideRequestFetch,
    isPremiumPDPEnabled,
    isEnhancedContentPresent: hasEnhancedContent,
    isEnhancedPDPReady,
    isDefaultPDPFetched,
    isProductDetailsQueryFetched,
    isProductDetailsQueryFetching,
  });

  useEffect(() => {
    const handleStoreChanged = () => {
      setStoreChanged(true);
    };
    root.window.addEventListener('preferredStoreUpdated', handleStoreChanged);
    return () => {
      root.window.removeEventListener('preferredStoreUpdated', handleStoreChanged);
    };
  }, []);

  useEffect(() => {
    if (addingToCartStatus === 'pending') {
      setPreventLoadingSkeletonFromShowing(true);
    } else if (isProductDetailsQuerySuccess) {
      setPreventLoadingSkeletonFromShowing(false);
    }
  }, [isProductDetailsQuerySuccess, addingToCartStatus]);

  useEffect(() => {
    if (addedCertonaItemObject) {
      setShowCertonaMiniCartFlyoutModal(true);
    }
  }, [addedCertonaItemObject]);

  useEffect(() => {
    if (trackQOHError) {
      if (addingToCartStatus === 'error') {
        setTrackQOHError(false);
        const qohErrorAnalyticsData: QohErrorAnalyticsData = {
          originalPartTypeId: productDetails?.originalPartTypeId ?? '',
          productAvailableQuantity: productAvailableQuantity ? productAvailableQuantity : 0,
          productBrandList: [skuDetailsData?.skuPricingAndAvailability?.brandName ?? ''],
          productCorePriceList: [
            skuDetailsData?.skuPricingAndAvailability?.corePrice.toString() ?? '',
          ],
          orderType: fulfillmentTypeId ?? '',
          skuNumber: skuDetailsData?.skuPricingAndAvailability?.skuId ?? '',
          partNumber: productDetails?.partNumber ?? '',
          productPrice: skuDetailsData?.skuPricingAndAvailability?.retailPrice ?? 0,
          productRepositoryId: productDetails?.ecommProductId ?? '',
          requestQuantity: Number(quantity ?? 0),
          errorMessage: addToCartErr ?? '',
        };
        trackPdpQoHFailure(qohErrorAnalyticsData);
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addingToCartStatus, trackQOHError]);
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  });

  useEffect(() => {
    if (locale === countryCodes.ptBr) {
      return;
    }

    fetchPaymentOptions({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  const [activeCache, setActiveCache] = useState(false);
  const [showMiniCartModal, setShowMiniCartModal] = useState(false);
  const [showMiniCartFlyoutModal, setShowMiniCartFlyoutModal] = useState(false);
  const [preventLoadingSkeletonFromShowing, setPreventLoadingSkeletonFromShowing] = useState(false);
  const prevStoreNumber = usePrevious(headerData?.storeNumber ?? '');
  const prevVehicleId = usePrevious(vehicleId);
  const checkToOpenMiniCartModal =
    (cartSuccessNote || minicart) && (showMiniCartModal || showMiniCartFlyoutModal);
  const [showYmme, setShowYmme] = useState(false);
  const shouldShowLoadingSkeleton =
    !preventLoadingSkeletonFromShowing &&
    (isProductDetailsQueryFetching || skuDetailsResult.isFetching) &&
    !isRedirect;
  const isPdpCertonaAddToCartEnabled = useCertonaAddToCartFlag();
  const skuId = skuDetailsData?.skuPricingAndAvailability.skuId ?? '';
  const useNewRecommendationsApi = useMonetateDecisionFlag('personalization_engine_v2');
  const recommendationsProductId = productDetails?.itemId ?? skuId;
  const { data: newRecommendations } = useRecommendations({
    servingConfig: 'recommended_for_you',
    productId: recommendationsProductId,
    enabled: !!useNewRecommendationsApi && !!recommendationsProductId,
  });
  const fittingPartsURLPartsPro =
    pdpBreadcrumb?.find((item) => item.pageType === 'ProductShelf')?.seoUrl ?? routePaths.blankURL;
  const fittingPartsURLAces =
    pdpBreadcrumb?.find((item) => item.pageType === 'PartGroup')?.canonicalUrl ??
    routePaths.blankURL;
  const certonaType =
    isCertonaEnabledForOosOrDoesNotFit && (productNotAvailable || isOos || doesNotFit)
      ? certonaPageType.pencilpdp
      : !isAccessories
        ? certonaPageType.pdp
        : certonaPageType.accessoriesProductPage;
  const certonaRecommendations = useGetCertonaRecommendations(
    {
      certonaType,
      vehicleID: acesEnabled ? preferredVehicle?.partsProVehicleId?.toString() : vehicleId,
      Category1: parentShelfPage?.displayName,
      skuIds: skuId,
      parttype: acesEnabled
        ? productDetails?.ecommProductId
        : formatPartTypeId(productDetails?.originalPartTypeId),
    },
    [vehicleId, skuId, productNotAvailable, isOos, doesNotFit],
    Boolean(
      productDetailsQueryData &&
      !isRedirect &&
      skuId !== '' &&
      (!disableCertonaCall || prevVehicleId !== vehicleId),
    ),
  );

  const certonaRecommendationsFiltered = useNewRecommendationsApi
    ? certonaRecommendations?.filter(
        (r) => r.scheme !== 'partproduct_rr' && r.scheme !== 'accessoriesproduct_rr',
      )
    : certonaRecommendations;

  const mergedCertonaRecommendations =
    useNewRecommendationsApi && newRecommendations?.items && newRecommendations.items.length > 0
      ? [
          {
            display: 'true',
            items: newRecommendations.items,
            scheme: 'partproduct_rr',
            explanation: 'Recommended for You',
          },
          ...(certonaRecommendationsFiltered ?? []),
        ]
      : certonaRecommendationsFiltered;

  useAutoCloseOnOrderReady();

  useEffect(() => {
    const jsTrackTimeOut = setTimeout(() => {
      const sortFilterList = document.querySelectorAll('.bv-dropdown-title');

      if (sortFilterList?.length > 0) {
        // .forEach is not supported in IE
        for (const element of sortFilterList) {
          element.addEventListener('click', trackSortFilter);
        }
      }
    }, 4500);
    return function cleanUp() {
      clearProductPageData();
      root.sessionStorage?.removeItem('productType');
      clearTimeout(jsTrackTimeOut);
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (minicart || cartSuccessNote) {
      setDisableCertonaCall(true);

      if (isDesktop && !dealDrawerOpen && cartSuccessNote) {
        setShowMiniCartModal(true);
      }

      if (!dealDrawerOpen && cartSuccessNote) {
        setMiniCartFlyoutVisible(true);
        setShowMiniCartFlyoutModal(true);
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minicart, cartSuccessNote]);

  useEffect(() => {
    if (!activeCache) {
      setActiveCache(true);
    } // after the inital call, any updates will use active caching

    const storeNumber = headerData?.storeNumber ?? '';
    const vehicleId = acesEnabled
      ? (headerData?.vehicleMap?.vehicleId ?? '')
      : (headerData?.vehicleMap?.catalogVehicleId ?? '');
    const url = getPathnameFromAsPath(legacyRouter.asPath);
    const query = legacyRouter.query;

    if (activeCache && query.vehicleId && vehicleId !== prevVehicleId && !!prevVehicleId) {
      const formattedUrl = stringifyUrl(
        {
          url,
          query: { ...query, vehicleId: null },
        },
        {
          skipNull: true,
        },
      );
      setAnalyticsExecuted(false);
      router.push(formattedUrl);
    } else if (
      activeCache &&
      (storeNumber !== prevStoreNumber || vehicleId !== prevVehicleId || !prevVehicleId)
    ) {
      setAnalyticsExecuted(false);
      setDisableCertonaCall(false);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerData?.storeNumber, headerData?.vehicleMap]);

  useEffect(() => {
    if (legacyRouter.query.duplicateSKURedirect === 'true') {
      const query = {
        ...legacyRouter.query,
      };

      delete query.duplicateSKURedirect;
      delete query.slug;

      if (legacyRouter.replace) {
        void legacyRouter.replace(
          {
            pathname: window.location.pathname,
            query,
          },
          undefined,
          { shallow: true },
        );
      } else {
        const newUrl = stringifyUrl({
          url: window.location.pathname,
          query: Object.entries(query).reduce<Record<string, string | string[]>>(
            (acc, [key, val]) => {
              if (val !== undefined && val !== null) {
                acc[key] = Array.isArray(val) ? val : String(val);
              }
              return acc;
            },
            {},
          ),
        });
        router.replace(newUrl);
      }
    }
    setShowMiniCartModal(false);
    setShowMiniCartFlyoutModal(false);
    setShowCertonaMiniCartFlyoutModal(false);
    setDisableCertonaCall(false);
    setAnalyticsExecuted(false);
    clearProductPageData();
    setShippingTypeChanged(false);
    sessionStorage.removeItem('enhancedPDPContent');

    return () => {
      sessionStorage.removeItem('enhancedPDPContent');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legacyRouter.asPath, dispatch, clearProductPageData, vehicleId, router]);

  useEffect(() => {
    if (sessionStorage.getItem('oemPartNumber')) {
      setOemPartNumber(sessionStorage.getItem('oemPartNumber')!);
    }
    if (sessionStorage.getItem('oemBrandName')) {
      setOemBrandName(sessionStorage.getItem('oemBrandName')!);
    }
    return () => {
      sessionStorage.removeItem('oemPartNumber');
      sessionStorage.removeItem('oemBrandName');
    };
  }, []);

  const submitToCart = () => {
    router.push(routePaths.cart);
  };

  const closeStoreModal = async () => {
    if (addedCertonaItemObject) {
      setCertonaRecommendationAddedToCart(false);
      setAddedCertonaItemObject(null);
    } else {
      clearProductPageData();
      setShowMiniCartModal(false);
      setMiniCartFlyoutVisible(false);
      setShowMiniCartFlyoutModal(false);
      setDisableCertonaCall(false);
    }
  };

  const [dealDrawerOpen, setDealDrawerOpen] = useState(false);
  const [autoFocus, setAutoFocus] = useState(false);

  const closeDealsDrawer = useCallback(() => {
    setShowYmme(false);
    setShowBundleDeals(false);
    isBundleDealDrawerOpen(false);
    setAutoFocus(true);
  }, []);

  const handleDealsAddToCart = useCallback(() => {
    setShippingTypeChanged(false);
    setDealsAddedToCart(true);
  }, []);

  const onContinueShopping = useCallback(async () => {
    await refetchProductData();
    await refetchSkuDetails();
  }, [refetchProductData, refetchSkuDetails]);

  if (isProductDetailsQueryError || skuDetailsResult.isError) {
    return <ErrorPage />;
  }

  const isBundleDealDrawerOpen = (open: boolean) => {
    setDealDrawerOpen(open);
  };

  const showWarrantyModalFromPD = (showModalFrom: string) => {
    setWarrantyModal({ isShown: true, showModalFrom });
  };

  const openConfigurableSku = () => setCustomizeModalOpen(true);

  const brandCode = acesEnabled
    ? ((productDetails as ProductModelACES)?.brandId ?? '')
    : (skuDetailsData?.skuPricingAndAvailability.configurableBrandCode ?? '');
  const productLineCode = skuDetailsData?.skuPricingAndAvailability.productLine ?? '';
  const renderProductCustomizableSKU = () => {
    if (productDetails) {
      const fixedHeaderWrapper = root.document.getElementById('TopHeader');

      if (fixedHeaderWrapper) {
        fixedHeaderWrapper.style.zIndex = '152';
      }

      return (
        <LazyProductCustomizableSKU
          preferredVehicle={preferredVehicle}
          count={count}
          shippingType={shippingType}
          skuId={skuId}
          storeNumber={storeNumber}
          brandCode={brandCode}
          productLineCode={productLineCode}
          productDetails={productDetails}
          customizeModalOpen={customizeModalOpen}
          setCustomizeModalOpen={setCustomizeModalOpen}
          productAvailableQuantity={productAvailableQuantity ?? 0}
        />
      );
    } else {
      return null;
    }
  };

  const renderMiniCart = () => {
    const productImage = productDetails?.itemImageUrl ?? '';
    const productTitle = productDetails?.itemDescription ?? '';

    const productDetailsForMiniCart = {
      productName: {
        productBrand: productDetails?.subBrandName,
        productPart: productDetails?.partTypeName,
        quickNote: productDetails?.quickNote,
      },
      productAvailabilityInfo: skuDetailsResult.data?.[0],
    };

    const renderOldMiniCart = () => {
      return (
        <Hidden only={['sm', 'md']}>
          {showMiniCartModal && (
            <Suspense>
              <LazyMiniCart
                isModalOpen={showMiniCartModal}
                image={productImage}
                successData={cartSuccessData}
                productDetails={productDetailsForMiniCart}
                closeStoreModal={closeStoreModal}
                cartUrl={submitToCart}
                productTitle={productTitle}
                pageName="pdp"
              />
            </Suspense>
          )}
        </Hidden>
      );
    };

    const renderNewFlyoutMiniCart = () => {
      return (
        <Drawer
          data-testid="mini-cart-flyout"
          anchor={'right'}
          open={miniCartFlyoutVisible}
          onClose={toggleMiniCartFlyout}
          PaperProps={{ className: styles.customPaperDrawer }}
          drawerRef={lazyMiniCartModalRef}
        >
          <TrapFocus open={miniCartFlyoutVisible}>
            <div
              className={styles.miniCartFlyoutDialog}
              role="dialog"
              aria-modal="true"
              aria-labelledby={MINI_CART_TITLE_ID}
            >
              <Suspense>
                {showMiniCartFlyoutModal && (
                  <LazyMiniCartFlyoutComp
                    handleClose={toggleMiniCartFlyout}
                    isModalOpen={showMiniCartFlyoutModal}
                    image={productImage}
                    successData={cartSuccessData}
                    productDetails={{
                      productName: productDetailsForMiniCart.productName,
                      productPartType: acesEnabled
                        ? productDetails?.partGroupId
                        : productDetails?.originalPartTypeId,
                      vehicleFitFlag: productDetails?.vehicleFitFlag,
                      productAvailabilityInfo: {
                        aZBadgesFlagVO:
                          productDetailsForMiniCart.productAvailabilityInfo?.aZBadgesFlagVO,
                        dealsInfo: undefined,
                        skuPricingAndAvailability:
                          productDetailsForMiniCart.productAvailabilityInfo
                            ?.skuPricingAndAvailability,
                      },
                      partTerminologyId: productDetails?.ecommProductId,
                    }}
                    miniCartData={miniCartMap}
                    closeStoreModal={closeStoreModal}
                    cartUrl={submitToCart}
                    productTitle={productTitle}
                    pageName="pdp"
                    orderDetailsFromState={orderDetailsFromState}
                    parentRef={lazyMiniCartModalRef}
                    pageLevelCertonaRecommendations={certonaRecommendations}
                  />
                )}
              </Suspense>
            </div>
          </TrapFocus>
        </Drawer>
      );
    };

    if (checkToOpenMiniCartModal) {
      if (
        (isDesktop && desktopMiniCartFlyoutEnabled) ||
        (isMobile && mobileMiniCartFlyoutEnabled) ||
        (isTablet && tabletMiniCartFlyoutEnabled)
      ) {
        return renderNewFlyoutMiniCart();
      } else if (!isMobile) {
        return renderOldMiniCart();
      }
    }
  };

  const PdpTopSection = isProductDetailsPageV2Enabled
    ? ProductAvailabilityInfoV2
    : ProductAvailabilityInfo;

  return (
    <div className={styles.pdpWrapper}>
      {isPageLoading && (
        <>
          <View gap={2} direction="row" padding={[4, 5]}>
            <View.Item>
              <Skeleton height="12px" width="50%" />
            </View.Item>
            <View.Item>/</View.Item>
            <View.Item>
              <Skeleton height="12px" width="100px" />
            </View.Item>
            <View.Item>/</View.Item>
            <View.Item>
              <Skeleton height="12px" width="100px" />
            </View.Item>
          </View>

          <View padding={{ s: [4, 0], m: [8, 0] }}>
            {isProductDetailsPageV2Enabled ? (
              <ProductAvailabilityInfoSkeletonV2
                leftColumn={<LeftColumnSkeleton />}
                middleColumn={<MiddleColumnSkeleton />}
                rightColumn={<RightColumnSkeleton />}
                middleTop={<MiddleColumnSkeletonTop />}
                middleBottom={<MiddleColumnSkeletonBottom />}
              />
            ) : (
              <ProductAvailabilityInfoSkeleton
                leftContent={<LeftSectionSkeletonLoading />}
                rightContent={<RightSectionSkeletonLoading />}
              />
            )}
          </View>
        </>
      )}

      <CMSModularHandler
        content={currentContent}
        certonaType={certonaType}
        certonaRecommendations={mergedCertonaRecommendations}
        productId={skuId}
        onCertonaRecommendationClick={() => {
          setRecommendationsClicked(true);
          setAnalyticsExecuted(false);
        }}
        data-testid="cms-modular-handler"
        setAddedCertonaItemObject={setAddedCertonaItemObject}
        ProductDetails={
          <PdpTopSection
            key={skuId}
            showWarrantyModal={showWarrantyModalFromPD}
            openConfigurableSku={openConfigurableSku}
            preferredVehicle={preferredVehicle}
            count={count}
            groupId={groupId}
            setShowYmmeDealsDrawer={setShowYmme}
            setGroupId={setGroupId}
            quantityInCart={quantityInCart}
            setQuantityInCart={setQuantityInCart}
            setShowBundleDeals={setShowBundleDeals}
            retailPrice={retailPrice}
            setRetailPrice={setRetailPrice}
            setCount={setCount}
            shippingType={shippingType}
            setShippingType={setShippingType}
            shippingTypeChanged={shippingTypeChanged}
            setShippingTypeChanged={setShippingTypeChanged}
            fulfillmentId={fulfillmentId}
            setFulfillmentId={setFulfillmentId}
            setShouldOpenSpecsPanel={setShouldOpenSpecsPanel}
            oemBrandName={oemBrandName}
            oemPartNumber={oemPartNumber}
            prevShippingType={prevShippingType ?? undefined}
            shouldShowSkeleton={shouldShowLoadingSkeleton}
            productDetails={productDetails}
            productDetailsSuccess={isProductDetailsQuerySuccess}
            productDetailsUpdatedAt={productDetailsQueryDataUpdatedAt}
            defaultFulfillmentOption={defaultFulfillmentOption}
            skuDealsData={skuDealsData}
            skuDetailsData={skuDetailsData}
            skuDetailsDataLastUpdatedAt={skuDetailsResult.dataUpdatedAt}
            setTrackQOHError={setTrackQOHError}
            changeFindProduct={changeFindProduct}
            refetchProductData={refetchProductData}
            refetchSkuDetails={refetchSkuDetails}
            hasVehicle={hasVehicle ?? false}
            pdpAddToCartSuccess={addingToCartStatus === 'success'}
            addToCartButtonLoading={
              addingToCartStatus === 'pending' ||
              orderDetailsReadyStatus === actionTypes.UPDATE_ORDERITEM_REQUESTING
            }
            isBundleDealDrawerOpen={isBundleDealDrawerOpen}
            autoFocus={autoFocus}
            onAddToCartCall={addToCartCall}
            productData={productData}
            xFusionQueryId={productDetailsQueryData?.xFusionQueryId}
            staticPrice={staticPrice}
            storeChanged={storeChanged}
            setStoreChanged={setStoreChanged}
          />
        }
        warrantyModal={warrantyModal}
        setWarrantyModal={setWarrantyModal}
        shouldOpenSpecsPanel={shouldOpenSpecsPanel}
        setShouldOpenSpecsPanel={setShouldOpenSpecsPanel}
        shouldShowLoadingSkeleton={shouldShowLoadingSkeleton}
        shippingType={shippingType}
        fulfillmentId={fulfillmentId}
        canRenderComparisonChart={canRenderCMSComparisonChart}
        setCertonaRecommendationAddedToCart={setCertonaRecommendationAddedToCart}
        oemPartNumber={oemPartNumber}
        isPremiumPDPEnabled={isPremiumPDPEnabled}
      />

      {isPDPContentFetched && (
        <>
          {isProductDetailsQueryFetched && productDetailsSEOMetadata ? (
            <ProductDetailMetadata
              title={productDetailsSEOMetadata.title}
              metaTitle={productDetailsSEOMetadata.metaTitle}
              url={productDetailsSEOMetadata.url}
              description={productDetailsSEOMetadata.description}
              image={productDetailsSEOMetadata.image}
              siteName={productDetailsSEOMetadata.siteName}
              canonicalURL={productDetailsSEOMetadata.canonicalURL}
              alternateURL={productDetailsSEOMetadata.alternateUrl}
              hrefLang={productDetailsSEOMetadata.refLanguage}
            />
          ) : null}
          {(acesEnabled
            ? productDetails?.configurableSkuFlag
            : (skuDetailsData?.skuPricingAndAvailability.configurableSku ?? false)) &&
            preferredVehicle &&
            customizeModalOpen &&
            renderProductCustomizableSKU()}
        </>
      )}
      <div className={styles.miniCartPopup}>{renderMiniCart()}</div>
      <div className={styles.miniCartPopup}>
        <Drawer
          data-testid="mini-cart-flyout"
          anchor={'right'}
          open={showCertonaMiniCartFlyoutModal}
          onClose={toggleCertonaMiniCartFlyout}
          PaperProps={{ className: styles.customPaperDrawer }}
          drawerRef={lazyMiniCartModalRef}
        >
          <TrapFocus open={showCertonaMiniCartFlyoutModal}>
            <div
              className={styles.miniCartFlyoutDialog}
              role="dialog"
              aria-modal="true"
              aria-labelledby="mini-cart-modal"
            >
              <Suspense>
                {showCertonaMiniCartFlyoutModal && addedCertonaItemObject && (
                  <LazyMiniCartFlyoutComp
                    handleClose={toggleCertonaMiniCartFlyout}
                    isModalOpen={showCertonaMiniCartFlyoutModal}
                    image={addedCertonaItemObject.productImage}
                    successData={addedCertonaItemObject.cartSuccessData}
                    productDetails={{
                      productName: addedCertonaItemObject.productDetails.productName,
                      certonaItemAddedTocart:
                        certonaRecommendationAddedToCart && isPdpCertonaAddToCartEnabled,
                      originalPartTypeId: addedCertonaItemObject.productDetails.partType,
                      productPartType: acesEnabled
                        ? productDetails?.partGroupId
                        : productDetails?.partTypeId,
                      productId: productDetails?.ecommProductId,
                      productAvailabilityInfo: {
                        aZBadgesFlagVO: null,
                        dealsInfo: undefined,
                        skuPricingAndAvailability: undefined,
                      },
                      partTerminologyId: productDetails?.ecommProductId,
                    }}
                    miniCartData={miniCartMap}
                    closeStoreModal={closeStoreModal}
                    cartUrl={addedCertonaItemObject.submitToCart}
                    productTitle={addedCertonaItemObject.productTitle}
                    pageName={addedCertonaItemObject.pageName}
                    orderDetailsFromState={orderDetailsFromState}
                    skuId={addedCertonaItemObject.productDetails.skuId}
                    parentRef={lazyMiniCartModalRef}
                    pageLevelCertonaRecommendations={certonaRecommendations}
                  />
                )}
              </Suspense>
            </div>
          </TrapFocus>
        </Drawer>
      </div>
      <SEOProductSchema />
      {productDetails?.itemId && (
        <FeatureFlag flag="DLM_PDP_ENABLED">
          <DynamicLinks seoRepositoryId={productDetails.itemId} />
        </FeatureFlag>
      )}
      <PreFooter />
      <DealDrawer
        analyticsPageType={analyticsPageType.catalogProductDetail}
        setShowYmme={setShowYmme}
        showYmme={showYmme}
        open={showBundleDeals}
        skuId={skuDetailsData?.skuPricingAndAvailability?.skuId}
        skuRetailPrice={retailPrice}
        skuFulfillmentTypeId={fulfillmentId}
        skuQuantity={quantityInCart || 1}
        skuIsVehicleSpecific={productDetails?.vehicleSpecificFlag ?? false}
        closeDealsDrawer={closeDealsDrawer}
        handleDealsAddToCart={handleDealsAddToCart}
        dealsAddedToCart={dealsAddedToCart}
        brand={productDetails?.subBrandName}
        partTypeId={productDetails?.originalPartTypeId}
        productRepositoryId={productDetails?.ecommProductId}
        onContinueShopping={onContinueShopping}
        dealConfigurableLabel={
          isNewDealsApiEnabled
            ? skuDealsData?.[0]?.skuDealsInfo?.dealConfigurableLabel
            : skuDetailsData?.skuPricingAndAvailability.dealConfigurableLabel
        }
        fittingPartsURL={acesEnabled ? fittingPartsURLAces : fittingPartsURLPartsPro}
        partGroupId={productDetails?.partGroupId}
        partTerminologyId={productDetails?.ecommProductId}
      />
    </div>
  );
}

export default ProductDetail;
